/**
 * 메일 관리자 패널 — 결재 관리자 모드처럼 '회사 전체' 메일을 열람한다.
 *
 * 두 모드 (사이드바 관리자 섹션의 두 항목이 각각 진입)
 *   - mode="all"       : 전 직원 메일(수신·발신) 페이지네이션 + 방향 탭 + 제목/보낸이 검색
 *   - mode="unmatched" : 어느 직원 받은편지함에도 배정되지 않은 수신 메일(그룹주소·매칭 실패)
 *                        → 행마다 '배정' 버튼으로 직원에게 수동 배정(reassign)
 *
 * 왜 필요한가
 *   수신 메일이 직원 매칭에 실패하면 mail_message 에는 있는데 아무 받은편지함에도 안 떠서
 *   '조용히 사라진 것처럼' 보였다. 이 화면이 그 사각지대를 관리자에게 드러낸다.
 *   행 클릭 = 관리자 열람(getMailAsAdmin) — READ_OTHER 감사 로그가 자동 기록된다.
 */
import React, { useEffect, useState } from "react";
import {
    Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
    Paper, Stack, Tab, TableBody, TableCell, TableHead, TableRow, Tabs,
    TextField, Tooltip, Typography, Button,
} from "@mui/material";
import SearchRoundedIcon  from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";

import { mailApi }   from "../../api/mailApi";
import { api }       from "../../../../util/api";
import { notify }    from "../../../../util/toast";
import { ds }        from "../../../../styles/designSystem";
import ActionButton  from "../../../../component/common/ActionButton";
import AppAutocomplete from "../../../../component/common/AppAutocomplete";
import LoadingDots   from "../../../../component/common/LoadingDots";
import TablePaginator, { DEFAULT_PAGE_SIZE, TableSizeSelect } from "../../../../component/common/TablePaginator";
import ResizableTable from "../../../../component/common/ResizableTable";
import dayjs from "dayjs";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const DIR_META = {
    INBOUND:  { label: "수신", color: "#2563eb", bg: "#dbeafe" },
    OUTBOUND: { label: "발신", color: "#16a34a", bg: "#dcfce7" },
};

const fmt = (v) => (v ? dayjs(v).format("YYYY.MM.DD HH:mm") : "-");

/**
 * '배정' 열 — 지금 이 메일을 편지함에 갖고 있는 사람 수.
 *
 * <p>0명이 두 가지 뜻이라 색을 나눈다.
 * <ul>
 *   <li><b>미배정</b>(빨강) — 한 번도 못 들어갔다. 그룹주소 수신·직원 매칭 실패.
 *       관리자가 손을 대야 하는 상태다.</li>
 *   <li><b>삭제됨</b>(회색) — 들어갔었는데 수신자가 영구삭제했다. 영구삭제는 메일이 아니라
 *       본인 user_state 행만 지우므로 배정 인원이 0이 된다. <b>정상적으로 처리가 끝난 것</b>이라
 *       할 일이 아니다. 이걸 빨간 '미배정' 으로 칠하면 관리자가 지워진 메일을 되살리려 든다.</li>
 * </ul>
 * 판정은 백엔드가 준 {@code everAssigned} (수신자 중 직원 매칭 기록 유무)로 한다.
 */
function AssignedCell({ mail }) {
    if (mail.matchedCount > 0) {
        return (
            <Typography sx={{ fontSize: 12.5, color: "#475569" }}>
                {mail.matchedCount}명
            </Typography>
        );
    }
    if (mail.everAssigned) {
        return (
            <Tooltip title="수신자가 영구삭제했습니다. 원본은 보관되어 있으며 다시 배정할 대상이 아닙니다">
                <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>삭제됨</Typography>
            </Tooltip>
        );
    }
    return (
        <Typography sx={{ fontSize: 12.5, color: "#dc2626", fontWeight: 700 }}>미배정</Typography>
    );
}

export default function AdminMailPanel({ mode = "all", onOpenMail }) {
    const unmatched = mode === "unmatched";

    const [rows, setRows]       = useState([]);
    const [total, setTotal]     = useState(0);
    const [page, setPage]       = useState(0);
    const [size, setSize]       = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(false);
    const [q, setQ]             = useState("");
    const [direction, setDirection] = useState("ALL");

    // 배정 다이얼로그 (unmatched 모드)
    const [assign, setAssign]     = useState(null);   // { mail, employee } | null
    const [employees, setEmployees] = useState([]);
    const [assigning, setAssigning] = useState(false);

    const load = async (overridePage = null, overrideQ = null, overrideDir = null) => {
        setLoading(true);
        try {
            const p = overridePage != null ? overridePage : page;
            const res = unmatched
                ? await mailApi.adminListUnmatched(p, size)
                : await mailApi.adminListAll(overrideDir != null ? overrideDir : direction,
                                             overrideQ != null ? overrideQ : q, p, size);
            const data = res?.data ?? res;
            setRows(data?.content || []);
            setTotal(data?.totalElements || 0);
        } catch (e) {
            notify(e?.response?.data?.message || "목록을 불러오지 못했습니다", "error");
            setRows([]); setTotal(0);
        } finally { setLoading(false); }
    };

    // 통합 재조회 effect — 검색어(debounce)·모드·방향·페이지크기 변화 모두 여기서 처리.
    // ⚠ mode 를 deps 에 넣는 이유: 사이드바에서 '전체 메일'↔'미배정 메일' 을 오가면 React 가
    //   같은 컴포넌트를 재사용해 state(rows/total)가 남는다 — mode 만 바뀌고 재조회가 없으면
    //   '전체 메일' 화면에 미배정 18건이 그대로 보이는 화면 거짓말이 된다(ContactsPanel 의
    //   initialScope 문제와 동일한 클래스).
    useEffect(() => {
        const t = setTimeout(() => { setPage(0); load(0); }, q ? 300 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q, mode, direction, size]);

    // 배정 다이얼로그 첫 오픈 시 직원 목록 로드(1회)
    useEffect(() => {
        if (!assign || employees.length) return;
        api.get(`${API_BASE}/employees`)
            .then(list => setEmployees(Array.isArray(list) ? list : (list?.content ?? [])))
            .catch(() => notify("직원 목록을 불러오지 못했습니다", "error"));
        // eslint-disable-next-line
    }, [assign]);

    const handleAssign = async () => {
        if (!assign?.mail || !assign?.employee) return;
        setAssigning(true);
        try {
            await mailApi.reassignMail(assign.mail.id, assign.employee.id);
            notify(`'${assign.employee.name}' 님의 받은편지함으로 배정했습니다`, "success");
            setAssign(null);
            await load();
        } catch (e) {
            notify(e?.response?.data?.message || "배정에 실패했습니다", "error");
        } finally { setAssigning(false); }
    };

    const COLS = unmatched ? 7 : 7;

    return (
        <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column",
                   overflow: "auto", ...ds.hiddenScrollbar }}>
            {/* 헤더 */}
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", flex: 1 }}>
                    {unmatched ? "미배정 메일" : "전체 메일"}
                    <Typography component="span" sx={{ ml: 1, fontSize: 12, color: "#94a3b8" }}>{total}건 · 관리자</Typography>
                </Typography>
                <TableSizeSelect value={size} onChange={(n) => { setSize(n); setPage(0); }} />
                <Tooltip title="새로고침">
                    <IconButton size="small" onClick={() => load()}>
                        <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1.5, lineHeight: 1.6 }}>
                {unmatched
                    ? "어느 직원 받은편지함에도 배정되지 않은 수신 메일입니다 (그룹 주소 수신, 직원 매칭 실패 등). '배정'으로 담당 직원에게 넣어주세요."
                    : "회사 전체 메일을 열람합니다. 행 클릭 시 열람 사실이 감사 로그(READ_OTHER)에 기록됩니다."}
            </Typography>

            {/* 방향 탭 + 검색 (전체 모드만) */}
            {!unmatched && (
                <>
                    <Tabs value={direction}
                        onChange={(e, v) => setDirection(v) /* 재조회는 통합 effect 가 담당 */}
                        sx={{ minHeight: 36, mb: 1.5,
                              "& .MuiTab-root": { minHeight: 36, fontSize: 13, fontWeight: 600, textTransform: "none" } }}>
                        <Tab value="ALL" label="전체" />
                        <Tab value="INBOUND" label="수신" />
                        <Tab value="OUTBOUND" label="발신" />
                    </Tabs>
                    <TextField
                        fullWidth size="small" value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="제목 / 보낸이 검색"
                        InputProps={{ startAdornment: <SearchRoundedIcon sx={{ fontSize: 18, color: "#94a3b8", mr: 1 }} /> }}
                        sx={{ mb: 2 }}
                    />
                </>
            )}

            {/* 테이블 — 표는 내부 스크롤 금지(패널 루트가 스크롤 담당, 시스템 공통 규칙) */}
            <Paper variant="outlined" sx={{ borderColor: "#e2e8f0", overflowX: "clip", overflowY: "visible", flexShrink: 0 }}>
                <ResizableTable size="small" sx={ds.tableSx} resetKey={`${mode}-${direction}`}>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" sx={{ width: 60, minWidth: 60 }}>번호</TableCell>
                            <TableCell align="center" sx={{ width: 64 }}>구분</TableCell>
                            <TableCell>보낸이</TableCell>
                            <TableCell>제목</TableCell>
                            <TableCell>수신자</TableCell>
                            <TableCell align="center">{unmatched ? "배정" : "배정 인원"}</TableCell>
                            <TableCell align="center">시각</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={COLS} align="center" sx={{ py: 4 }}><LoadingDots /></TableCell></TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow><TableCell colSpan={COLS} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 13 }}>
                                {unmatched ? "미배정 메일이 없습니다 👍" : (q ? "검색 결과가 없습니다" : "메일이 없습니다")}
                            </TableCell></TableRow>
                        ) : rows.map((m, idx) => {
                            const dm = DIR_META[m.direction] || { label: m.direction, color: "#64748b", bg: "#f1f5f9" };
                            return (
                                <TableRow key={m.id} hover onClick={() => onOpenMail?.(m.id)} sx={{ cursor: "pointer" }}>
                                    <TableCell align="center" sx={{ fontSize: 12, color: "#94a3b8" }}>{page * size + idx + 1}</TableCell>
                                    <TableCell align="center">
                                        <Chip label={dm.label} size="small"
                                              sx={{ fontSize: 11, fontWeight: 700, color: dm.color, bgcolor: dm.bg, height: 20 }} />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 13 }}>{m.fromName ? `${m.fromName} <${m.fromAddr || ""}>` : (m.fromAddr || "-")}</TableCell>
                                    <TableCell sx={{ fontSize: 13 }}>
                                        {m.subject || "(제목 없음)"}
                                        {m.hasAttachments ? <Typography component="span" sx={{ ml: 0.5, fontSize: 11, color: "#94a3b8" }}>📎{m.attachmentCount}</Typography> : null}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12.5, color: "#475569" }}>{m.toSummary || "-"}</TableCell>
                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                        {unmatched ? (
                                            <ActionButton
                                                variant="add"
                                                startIcon={<PersonAddAlt1RoundedIcon sx={{ fontSize: 15 }} />}
                                                onClick={() => setAssign({ mail: m, employee: null })}
                                                label="배정"
                                            />
                                        ) : (
                                            <AssignedCell mail={m} />
                                        )}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                                        {fmt(m.receivedAt || m.sentAt)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ResizableTable>
            </Paper>

            <TablePaginator
                total={total}
                pageSize={size}
                page={page + 1}
                onPageChange={(p) => { const np = p - 1; setPage(np); load(np); }}
            />

            {/* 배정 다이얼로그 — 직원 선택 후 reassign */}
            <Dialog open={Boolean(assign)} onClose={() => setAssign(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>메일 배정</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 12.5, color: "#64748b", mb: 1.5 }}>
                        "{assign?.mail?.subject || "(제목 없음)"}" 메일을 배정할 직원을 선택하세요.
                        배정하면 그 직원의 받은편지함에 나타납니다.
                    </Typography>
                    <AppAutocomplete
                        options={employees}
                        value={assign?.employee || null}
                        onChange={(v) => setAssign(a => ({ ...a, employee: v && typeof v !== "string" ? v : null }))}
                        getOptionLabel={(o) => (typeof o === "string" ? o : (o?.name ? `${o.name}${o.deptName ? ` · ${o.deptName}` : ""}` : ""))}
                        isOptionEqualToValue={(a, b) => a?.id === b?.id}
                        placeholder="직원 이름 검색"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAssign(null)} disabled={assigning} sx={{ textTransform: "none", color: "#64748b" }}>취소</Button>
                    <Button onClick={handleAssign} variant="contained" disabled={assigning || !assign?.employee}
                            sx={{ textTransform: "none", fontWeight: 700 }}>
                        {assigning ? "배정 중..." : "배정"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
